import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_URL } from "@/lib/api";
import { fmtCurrency, fmtHours, fmtPct, clientCurrency } from "@/lib/calc";
import StatusPill from "@/components/StatusPill";
import { useLayout } from "@/components/AppLayout";
import { Search, ChevronRight, ArrowUp, ArrowDown, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const filters = [
  { key: "all", label: "All" },
  { key: "on_track", label: "On Track" },
  { key: "close_to_limit", label: "Close to Limit" },
  { key: "losing_money", label: "Losing Money" },
];

const STATUS_ORDER = { losing_money: 0, close_to_limit: 1, on_track: 2 };

const COLS = [
  { key: "name", label: "Client", align: "left" },
  { key: "service_type", label: "Service Type", align: "left", hideOnMobile: true },
  { key: "monthly_fee", label: "Monthly Fee", align: "right" },
  { key: "logged_hours", label: "Hours Used", align: "right" },
  { key: "hours_diff", label: "Hours Left / Over", align: "right" },
  { key: "earned_to_date", label: "Earned to Date", align: "right" },
  { key: "estimated_profit", label: "Profit", align: "right", hideOnMobile: true },
  { key: "profit_margin", label: "Margin", align: "right", hideOnMobile: true },
  { key: "status", label: "Status", align: "left" },
];

const Retainers = () => {
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({ currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { refreshKey, openAddClient } = useLayout();

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/clients"), api.get("/settings")]);
      setClients(Array.isArray(c.data) ? c.data : []);
      setSettings(s.data || { currency: "USD" });
    } catch {
      toast.error("Failed to load retainers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/clients/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created = 0, skipped = [], samples_cleared = 0 } = r.data || {};
      if (created > 0) toast.success(`Imported ${created} retainer${created === 1 ? "" : "s"}`);
      if (skipped.length > 0) toast.warning(`${skipped.length} row${skipped.length === 1 ? "" : "s"} skipped`);
      if (created === 0 && skipped.length === 0) toast.info("Nothing to import");
      if (samples_cleared) {
        toast.info("Sample retainers removed — you've got the real thing now.", { duration: 6000 });
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const computedClients = useMemo(() => {
    return (clients || []).map((c) => ({
      ...c,
      hours_diff: (c?.included_hours ?? 0) - (c?.logged_hours ?? 0),
    }));
  }, [clients]);

  const filtered = useMemo(() => {
    const base = computedClients
      .filter((c) => (filter === "all" ? true : c?.status === filter))
      .filter((c) => (q ? (c?.name || "").toLowerCase().includes(q.toLowerCase()) : true));

    const sorted = [...base].sort((a, b) => {
      let av;
      let bv;
      if (sortBy === "status") {
        av = STATUS_ORDER[a.status] ?? 99;
        bv = STATUS_ORDER[b.status] ?? 99;
      } else if (sortBy === "name" || sortBy === "service_type") {
        av = (a[sortBy] || "").toLowerCase();
        bv = (b[sortBy] || "").toLowerCase();
      } else {
        av = a[sortBy] ?? 0;
        bv = b[sortBy] ?? 0;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [computedClients, filter, q, sortBy, sortDir]);

  const currency = settings?.currency || "USD";
  const counts = {
    all: clients.length,
    on_track: clients.filter((c) => c.status === "on_track").length,
    close_to_limit: clients.filter((c) => c.status === "close_to_limit").length,
    losing_money: clients.filter((c) => c.status === "losing_money").length,
  };

  const handleExport = () => {
    const link = document.createElement("a");
    link.href = `${API_URL}/clients/export`;
    link.target = "_self";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6" data-testid="retainers-page">
      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1 bg-white border border-[#E4DFD2] rounded-lg p-1">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f.key
                  ? "bg-[#1F2326] text-[#F8F6F1]"
                  : "text-[#69747C] hover:text-[#1F2326]"
              }`}
            >
              {f.label}
              <span
                className={`ml-2 text-[10px] tabular-nums ${
                  filter === f.key ? "text-[#C9C2B0]" : "text-[#A9A294]"
                }`}
              >
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-[#A9A294] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
              placeholder="Search clients..."
              className="w-full md:w-72 pl-9 pr-3 py-2 bg-white border border-[#E4DFD2] rounded-lg text-sm placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326] transition-colors"
            />
          </div>
          <button
            type="button"
            onClick={handleExport}
            data-testid="clients-export-btn"
            className="bg-white text-[#1F2326] border border-[#E4DFD2] hover:bg-[#FBFAF6] hover:border-[#D6CFB9] px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
            title="Download all clients as CSV"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImport}
            className="hidden"
            data-testid="clients-import-file"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="clients-import-btn"
            className="bg-white text-[#1F2326] border border-[#E4DFD2] hover:bg-[#FBFAF6] hover:border-[#D6CFB9] disabled:opacity-60 px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
            title="Import a CSV of retainers"
          >
            <Upload className="w-3.5 h-3.5" /> {importing ? "Importing..." : "Import"}
          </button>
        </div>
      </div>

      {/* Table — desktop / tablet (md and up) */}
      <div className="hidden md:block bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-full-table">
            <thead>
              <tr className="text-[11px] tracking-wider uppercase text-[#69747C] bg-[#FBFAF6]/60">
                {COLS.map((col) => {
                  const isActive = sortBy === col.key;
                  const ArrowIcon = sortDir === "asc" ? ArrowUp : ArrowDown;
                  const alignCls = col.align === "right" ? "text-right" : "text-left";
                  const hide = col.hideOnMobile ? "hidden lg:table-cell" : "";
                  return (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      data-testid={`sort-${col.key}`}
                      className={`font-semibold py-3.5 px-4 first:px-6 cursor-pointer select-none hover:text-[#1F2326] ${alignCls} ${isActive ? "text-[#1F2326]" : ""} ${hide}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {isActive ? <ArrowIcon className="w-3 h-3" strokeWidth={2.5} /> : null}
                      </span>
                    </th>
                  );
                })}
                <th className="py-3.5 px-4 text-right">
                  <span className="text-[#69747C]">View</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="px-6 py-10 text-center text-sm text-[#69747C]">
                    Loading.
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center">
                    <div className="text-[#1F2326] text-base font-medium">No retainers match this filter</div>
                    <div className="text-[#69747C] text-sm mt-1">Add your first client to see hours, cost, profit, and status.</div>
                    <button
                      onClick={() => openAddClient()}
                      data-testid="empty-add-btn"
                      className="mt-4 bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
                    >
                      + Add client
                    </button>
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((c) => {
                  const isOver = c.hours_diff < 0;
                  const cur = clientCurrency(c, currency);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/retainers/${c.id}`)}
                      className="border-t border-[#EDE7D6] hover:bg-[#FBFAF6]/60 cursor-pointer transition-colors"
                      data-testid={`clients-row-${c.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="font-semibold text-[#1F2326]">{c.name}</div>
                      </td>
                      <td className="px-4 py-4 text-[#69747C] hidden lg:table-cell">
                        {c.service_type || "—"}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums font-medium text-[#1F2326] whitespace-nowrap">
                        {fmtCurrency(c.monthly_fee, cur)}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-[#1F2326] whitespace-nowrap">
                        {fmtHours(c.logged_hours)}
                        <span className="text-[#A9A294] ml-1">/ {fmtHours(c.included_hours)}</span>
                      </td>
                      <td className={`px-4 py-4 text-right tabular-nums font-medium whitespace-nowrap ${isOver ? "text-[#7E332D]" : "text-[#1F2326]"}`}>
                        {isOver ? `Over by ${fmtHours(Math.abs(c.hours_diff))}` : `${fmtHours(c.hours_diff)} left`}
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums text-[#1F2326] whitespace-nowrap">
                        {fmtCurrency(c.earned_to_date || 0, cur)}
                        <div className="text-[10px] text-[#69747C] mt-0.5">
                          {fmtPct(c.value_used_pct || 0)} used
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right tabular-nums hidden lg:table-cell whitespace-nowrap text-[#1F2326]">
                        {fmtCurrency(c.estimated_profit, cur)}
                      </td>
                      <td
                        className={`px-4 py-4 text-right tabular-nums hidden lg:table-cell font-medium whitespace-nowrap ${
                          c.profit_margin >= 35
                            ? "text-[#42624B]"
                            : c.profit_margin >= 15
                            ? "text-[#7A5E1F]"
                            : "text-[#7E332D]"
                        }`}
                      >
                        {fmtPct(c.profit_margin)}
                      </td>
                      <td className="px-4 py-4">
                        <StatusPill status={c.status} />
                      </td>
                      <td className="px-4 py-4 text-right">
                        <ChevronRight className="w-4 h-4 text-[#C9C2B0] inline-block" />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards (below md) — one card per retainer with the most important numbers first */}
      <div className="md:hidden space-y-3" data-testid="clients-mobile-cards">
        {loading && (
          <div className="bg-white border border-[#E4DFD2] rounded-xl p-6 text-sm text-[#69747C]">
            Loading.
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-[#E4DFD2] rounded-xl p-6 text-center">
            <div className="text-[#1F2326] text-base font-medium">No retainers match this filter</div>
            <div className="text-[#69747C] text-sm mt-1">Add your first client to see hours, cost, profit, and status.</div>
            <button
              onClick={() => openAddClient()}
              data-testid="empty-add-btn-mobile"
              className="mt-4 bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"
            >
              + Add client
            </button>
          </div>
        )}
        {!loading &&
          filtered.map((c) => {
            const isOver = c.hours_diff < 0;
            const cur = clientCurrency(c, currency);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/retainers/${c.id}`)}
                data-testid={`clients-card-${c.id}`}
                className="w-full text-left bg-white border border-[#E4DFD2] rounded-xl p-4 hover:border-[#B89B5E] transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#1F2326] truncate">{c.name}</div>
                    {c.service_type ? (
                      <div className="text-xs text-[#69747C] mt-0.5">{c.service_type}</div>
                    ) : null}
                  </div>
                  <StatusPill status={c.status} />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C]">Monthly Fee</div>
                    <div className="tabular-nums font-medium text-[#1F2326]">{fmtCurrency(c.monthly_fee, cur)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C]">Hours Used</div>
                    <div className="tabular-nums text-[#1F2326]">
                      {fmtHours(c.logged_hours)}
                      <span className="text-[#A9A294]"> / {fmtHours(c.included_hours)}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C]">
                      {isOver ? "Over" : "Left"}
                    </div>
                    <div className={`tabular-nums font-medium ${isOver ? "text-[#7E332D]" : "text-[#1F2326]"}`}>
                      {isOver ? `${fmtHours(Math.abs(c.hours_diff))}` : `${fmtHours(c.hours_diff)}`}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C]">Earned to Date</div>
                    <div className="tabular-nums text-[#1F2326]">
                      {fmtCurrency(c.earned_to_date || 0, cur)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
};

export default Retainers;
